import { createAsyncThunk, createSlice, PayloadAction } from '@reduxjs/toolkit'

import { landAxios } from '@Root/utils/landAxios'
import MyHouse from '@Types/entities/MyHouse'
import MyHouseTerms from '@Types/entities/MyHouseTerms'
import MyhouseRegisterBuildingHo from '@Types/inner/myhouse/MyhouseRegisterBuildingHo'
import ComplexPyeong from '@Types/entities/ComplexPyeong'
import Address from '@Types/entities/Address'
import FetchResponse from '@Types/inner/FetchResponse'
import MyhouseMainHouseResponse from '@Types/entities/MyhouseMainHouseResponse'
import { FETCH_STATUS } from '@Root/contants'
import MyHouseTermsWithVersion from '@Types/entities/MyHouseTermsWithVersion'
import MyHouseExist from '@Types/entities/MyHouseExist'

export const fetchMyhouseRegister = createAsyncThunk('myhouseRegister/fetchMyhouseRegister', async (myhouseNo: number) => await (await landAxios.get(`/myhouse/${myhouseNo}`)).data)

export const fetchHouse = createAsyncThunk('myhouseRegister/register/fetchHouse', async ({ houseNo, complexNo }: { houseNo: number; complexNo?: number }) => {
  return await (await landAxios.get(`/myhouse/v2/${houseNo}`, { params: { complexNo: complexNo || 0 } })).data
})

export const fetchPrivacy = createAsyncThunk('myhouseRegister/privacy', async () => await (await landAxios.get(`/myhouse/v2/privacy`)).data)

export const fetchMyHouseExist = createAsyncThunk('myhouse/exist', async () => {
  return await (await landAxios.get(`/myhouse/v2/exist`)).data
})

export type MyHouseRegisterState = FetchResponse<MyHouse> &
  MyHouseTerms & {
    house: FetchResponse<MyhouseMainHouseResponse>
    privacy: FetchResponse<MyHouseTermsWithVersion>
    isTrendPrice: boolean
    myHouse: FetchResponse<MyHouseExist>
  }
const initialState: MyHouseRegisterState = {
  house: {
    fetchStatus: FETCH_STATUS.INITIAL,
  },
  privacy: {
    fetchStatus: FETCH_STATUS.INITIAL,
  },
  myHouse: {
    fetchStatus: FETCH_STATUS.INITIAL,
  },
} as MyHouseRegisterState

const myHouseRegisterSlice = createSlice({
  name: 'myhouse/register',
  initialState,
  reducers: {
    updateRealestateType: (state: MyHouseRegisterState, action: PayloadAction<string>) => {
      state.realestateTypeCode = action.payload
    },
    updatePyeong: (state: MyHouseRegisterState, action: PayloadAction<{ pyeongName: string; pyeongNo?: number }>) => {
      state.pyeongName = action.payload.pyeongName
      state.pyeongNo = action.payload.pyeongNo || initialState.pyeongNo
    },
    updateTradeType: (state: MyHouseRegisterState, action: PayloadAction<string>) => {
      state.tradeTypeCode = action.payload
      state.dealAmount = initialState.dealAmount
      state.warrantyAmount = initialState.warrantyAmount
      state.rentAmount = initialState.rentAmount
      state.tradeDate = initialState.tradeDate
      state.tradeEndDate = initialState.tradeEndDate
    },
    updateDealAmount: (state: MyHouseRegisterState, action: PayloadAction<number>) => {
      state.dealAmount = action.payload
    },
    updateWarrantyAmount: (state: MyHouseRegisterState, action: PayloadAction<number>) => {
      state.warrantyAmount = action.payload
    },
    updateRentAmount: (state: MyHouseRegisterState, action: PayloadAction<number>) => {
      state.rentAmount = action.payload
    },
    updateTradeDate: (state: MyHouseRegisterState, action: PayloadAction<string>) => {
      state.tradeDate = action.payload
    },
    updateTradeEndDate: (state: MyHouseRegisterState, action: PayloadAction<string>) => {
      state.tradeEndDate = action.payload
    },
    updateInitTradeDate: (state: MyHouseRegisterState) => {
      state.tradeDate = initialState.tradeDate
    },
    updateInitTradeEndDate: (state: MyHouseRegisterState) => {
      state.tradeEndDate = initialState.tradeEndDate
    },
    updatePrivacyCollectUsage: (state: MyHouseRegisterState, action: PayloadAction<boolean>) => {
      state.agreePrivacyCollectUsage = action.payload
    },
    updateSearchAddress: (state: MyHouseRegisterState, action: PayloadAction<{ address: Address; buildingHo: MyhouseRegisterBuildingHo; pyeong?: ComplexPyeong }>) => {
      const address = action.payload.address
      const buildingHo = action.payload.buildingHo
      const pyeong = action.payload.pyeong
      state.baseAddress = address.baseAddress
      state.abbreviationAddress = address.abbreviationAddress
      state.exposureArea = address.exposureArea
      state.realestateTypeCode = address.realestateTypeCode
      state.cortarNo = Number(address.cortarNo)
      state.complexNo = Number(address.complexNo)
      state.complexName = address.complexName
      state.buildingNo = buildingHo.buildingNo
      state.buildingName = buildingHo.buildingName
      state.hoNo = buildingHo.hoNo
      state.hoName = buildingHo.hoName
      state.pyeongNo = (pyeong && pyeong.pyeongNo) || initialState.pyeongNo
      state.pyeongName = (pyeong && pyeong.pyeongName) || initialState.pyeongName
    },
  },
  extraReducers: {
    [fetchMyhouseRegister.fulfilled.type]: (state: MyHouseRegisterState, action: PayloadAction<MyHouse>) => {
      Object.assign(state, action.payload)
      state.fetchStatus = FETCH_STATUS.SUCCESS
    },
    [fetchMyhouseRegister.rejected.type]: (state: MyHouseRegisterState) => {
      state.fetchStatus = FETCH_STATUS.FAILURE
    },
    [fetchMyhouseRegister.pending.type]: (state: MyHouseRegisterState) => {
      state.fetchStatus = FETCH_STATUS.PENDING
    },
    [fetchHouse.fulfilled.type]: (state: MyHouseRegisterState, action: PayloadAction<MyhouseMainHouseResponse>) => {
      state.house = {
        ...action.payload,
        fetchStatus: FETCH_STATUS.SUCCESS,
      }
    },
    [fetchHouse.pending.type]: (state: MyHouseRegisterState) => {
      state.house.fetchStatus = FETCH_STATUS.PENDING
    },
    [fetchHouse.rejected.type]: (state: MyHouseRegisterState) => {
      state.house.fetchStatus = FETCH_STATUS.FAILURE
    },
    [fetchPrivacy.fulfilled.type]: (state: MyHouseRegisterState, action: PayloadAction<MyHouseTermsWithVersion>) => {
      state.privacy = {
        ...action.payload,
        fetchStatus: FETCH_STATUS.SUCCESS,
      }
    },
    [fetchPrivacy.pending.type]: (state: MyHouseRegisterState) => {
      state.privacy.fetchStatus = FETCH_STATUS.PENDING
    },
    [fetchPrivacy.rejected.type]: (state: MyHouseRegisterState) => {
      state.privacy.fetchStatus = FETCH_STATUS.FAILURE
    },
    [fetchMyHouseExist.fulfilled.type]: (state: MyHouseRegisterState, action: PayloadAction<MyHouseExist>) => {
      state.myHouse.isMyHouseExist = action.payload.isMyHouseExist
      state.myHouse.fetchStatus = FETCH_STATUS.SUCCESS
    },
    [fetchMyHouseExist.pending.type]: (state: MyHouseRegisterState, action: PayloadAction<MyHouseExist>) => {
      state.myHouse.fetchStatus = FETCH_STATUS.PENDING
    },
    [fetchMyHouseExist.rejected.type]: (state: MyHouseRegisterState, action: PayloadAction<MyHouseExist>) => {
      state.myHouse.fetchStatus = FETCH_STATUS.FAILURE
    },
  },
})

export const {
  updateRealestateType,
  updatePyeong,
  updateTradeType,
  updateDealAmount,
  updateWarrantyAmount,
  updateRentAmount,
  updateTradeDate,
  updateInitTradeDate,
  updateTradeEndDate,
  updateInitTradeEndDate,
  updatePrivacyCollectUsage,
  updateSearchAddress,
} = myHouseRegisterSlice.actions
export default myHouseRegisterSlice.reducer
